import React, { useEffect, useState } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import { buildApiUrl, useBaseUrl } from '../../../utils/utils';
import localforage from 'localforage';
import Swal from 'sweetalert2';

interface Protocol {
    id: number;
    name: string;
    specialty: string;
    category: string;
    version: string;
    source: string;
    description: string;
    steps: string[];
    status: string;
    last_updated: string;
    is_network: boolean;
    facility_name: string | null;
    adopted_by_count: number;
    adopted_by_me: boolean;
}

const ClinicalProtocols: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<'browse' | 'adopted'>('browse');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSpecialty, setSelectedSpecialty] = useState('all');

    const [protocols, setProtocols] = useState<Protocol[]>([]);
    const [adopted, setAdopted] = useState<Protocol[]>([]);
    const [loading, setLoading] = useState(true);
    const [adopting, setAdopting] = useState<number | null>(null);

    useEffect(() => {
        document.title = "Clinical Protocols - Uzima Healthcare";
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, []);

    const fetchAll = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const [pRes, aRes] = await Promise.all([
                fetch(buildApiUrl(baseUrl, `ehr/clinical-protocols?clinic_id=${clinicId}`)),
                fetch(buildApiUrl(baseUrl, `ehr/clinical-protocols/adopted?clinic_id=${clinicId}`)),
            ]);
            const pData = await pRes.json();
            const aData = await aRes.json();
            if (pData.status === 'success') setProtocols(pData.data || []);
            if (aData.status === 'success') setAdopted(aData.data || []);
        } catch { /* leave lists empty on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    const adoptProtocol = async (protocol: Protocol) => {
        if (!clinicId) return;
        setAdopting(protocol.id);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `ehr/clinical-protocols/${protocol.id}/adopt`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId, staff_id: staffId }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                Swal.fire({ icon: 'success', title: data.message, timer: 1200, showConfirmButton: false });
                fetchAll();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to adopt protocol.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while adopting protocol.' });
        } finally {
            setAdopting(null);
        }
    };

    const list = activeTab === 'adopted' ? adopted : protocols;
    const specialties = ['all', ...Array.from(new Set(protocols.map(p => p.specialty).filter(Boolean)))];
    const filtered = list.filter(p =>
        (selectedSpecialty === 'all' || p.specialty === selectedSpecialty) &&
        (!searchQuery.trim() || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.specialty.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const totalAdoptions = protocols.reduce((s, p) => s + p.adopted_by_count, 0);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">Clinical Protocols</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/dashboard/clinician">Clinician</a></li>
                                            <li className="breadcrumb-item active">Clinical Protocols</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="alert alert-info alert-dismissible fade show" role="alert">
                                    <i className="ri-git-branch-line me-2"></i>
                                    <strong>Network-wide protocols:</strong> Clinical pathways shared across the Uzima network. Adopt evidence-based protocols used by leading facilities.
                                    <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {[
                                { label: 'Available Protocols', value: String(protocols.length), icon: 'ri-git-branch-line', color: 'primary' },
                                { label: 'Adopted by You', value: String(adopted.length), icon: 'ri-check-double-line', color: 'success' },
                                { label: 'Total Network Adoptions', value: String(totalAdoptions), icon: 'ri-team-line', color: 'info' },
                            ].map((stat, i) => (
                                <div className="col-xl-4 col-md-6" key={i}>
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <p className="text-uppercase fw-medium text-muted mb-0">{stat.label}</p>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <h4 className="fs-22 fw-semibold mb-0">{stat.value}</h4>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className={`avatar-title bg-${stat.color}-subtle rounded fs-3`}>
                                                        <i className={`${stat.icon} text-${stat.color}`}></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <ul className="nav nav-tabs-custom card-header-tabs border-bottom-0">
                                    {[
                                        { key: 'browse', label: 'Browse Protocols', icon: 'ri-search-line' },
                                        { key: 'adopted', label: 'My Adopted', icon: 'ri-bookmark-line' },
                                    ].map(tab => (
                                        <li className="nav-item" key={tab.key}>
                                            <a className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                               href="#" onClick={(e) => { e.preventDefault(); setActiveTab(tab.key as any); }}>
                                                <i className={`${tab.icon} me-1`}></i> {tab.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="card-body">
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <input type="text" className="form-control" placeholder="Search protocols by name or specialty..."
                                               value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                    </div>
                                    <div className="col-md-3">
                                        <select className="form-select" value={selectedSpecialty} onChange={(e) => setSelectedSpecialty(e.target.value)}>
                                            {specialties.map(s => <option key={s} value={s}>{s === 'all' ? 'All Specialties' : s}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                    </div>
                                ) : filtered.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-git-branch-line" style={{ fontSize: '2.5rem' }}></i>
                                        <h5 className="mt-3">{activeTab === 'adopted' ? "You haven't adopted any protocols yet" : 'No protocols found'}</h5>
                                        <p className="text-muted">
                                            {activeTab === 'adopted' ? 'Browse protocols and adopt the ones relevant to your practice.' : 'Try adjusting your search or specialty filter.'}
                                        </p>
                                    </div>
                                ) : (
                                    filtered.map(protocol => (
                                        <div className="card border shadow-none mb-3" key={protocol.id}>
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <h6 className="mb-0">{protocol.name}</h6>
                                                            <span className="badge bg-primary-subtle text-primary">{protocol.specialty}</span>
                                                            <span className="badge bg-light text-body">{protocol.category}</span>
                                                            {!protocol.is_network && protocol.facility_name && (
                                                                <span className="badge bg-secondary-subtle text-secondary">{protocol.facility_name}</span>
                                                            )}
                                                        </div>
                                                        <p className="text-muted small mb-2">{protocol.description}</p>
                                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                                            {protocol.steps.map((step, i) => (
                                                                <span key={i} className="badge bg-light text-body border">
                                                                    <span className="text-primary fw-bold me-1">{i + 1}.</span>{step}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <div className="d-flex gap-3 text-muted small">
                                                            {protocol.source && <span><i className="ri-book-open-line me-1"></i>{protocol.source}</span>}
                                                            <span><i className="ri-price-tag-3-line me-1"></i>v{protocol.version}</span>
                                                            <span><i className="ri-calendar-line me-1"></i>Updated {new Date(protocol.last_updated).toLocaleDateString()}</span>
                                                            <span><i className="ri-team-line me-1"></i>{protocol.adopted_by_count} clinic{protocol.adopted_by_count === 1 ? '' : 's'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="ms-3">
                                                        {protocol.adopted_by_me ? (
                                                            <span className="btn btn-sm btn-soft-secondary disabled"><i className="ri-check-line me-1"></i>Adopted</span>
                                                        ) : (
                                                            <button className="btn btn-sm btn-soft-success" disabled={adopting === protocol.id}
                                                                    onClick={() => adoptProtocol(protocol)}>
                                                                <i className="ri-bookmark-line me-1"></i>{adopting === protocol.id ? 'Adopting...' : 'Adopt'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </div>
        </>
    );
};

export default ClinicalProtocols;
